import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { Pencil, Trash2, FolderOpen } from "lucide-react";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { resolveGoogleLink, GOOGLE_HINT } from "@/components/admin/googleLink";
import { DANCE_OPTIONS } from "@/constants/dances";
import { toImageUrl } from "@/lib/driveUrl";

const KINDS = [
  { value: "photo", label: "Photo", hint: "Collez l'adresse de l'image (https://...)" },
  { value: "video", label: "Vidéo", hint: "Collez le lien YouTube de la vidéo (https://www.youtube.com/watch?v=...)" },
  { value: "audio", label: "Enregistrement audio", hint: "Collez le lien Google Drive du fichier audio (partage public) ou un lien direct .mp3" },
];

const EMPTY = { kind: "photo", title: "", url: "", description: "", dance: "" };
const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

export default function MediaManager() {
  const { refresh } = useSite();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [albumUrl, setAlbumUrl] = useState("");
  const [albumSaving, setAlbumSaving] = useState(false);
  const [albumTesting, setAlbumTesting] = useState(false);
  const [galleryRandom, setGalleryRandom] = useState(true);
  const [modeSaving, setModeSaving] = useState(false);

  const load = () => api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/settings").then((r) => {
      setAlbumUrl(r.data.gallery_album_url || "");
      setGalleryRandom(r.data.gallery_random !== false);
    }).catch(() => {});
  }, []);

  const saveGalleryMode = async () => {
    setModeSaving(true);
    try {
      await api.put("/admin/settings", { gallery_random: galleryRandom });
      refresh();
      toast.success("Mode d'affichage de la galerie enregistré");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setModeSaving(false);
    }
  };

  const saveAlbum = async () => {
    setAlbumSaving(true);
    try {
      await api.put("/admin/settings", { gallery_album_url: albumUrl });
      toast.success("Album enregistré — les photos et vidéos s'affichent sur la page Galerie");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAlbumSaving(false);
    }
  };

  const testAlbum = async () => {
    setAlbumTesting(true);
    try {
      const { data } = await api.post("/admin/album-test", { url: albumUrl });
      toast.success(`Lien valide : ${data.count} élément(s) trouvé(s) — ${data.photos} photo(s), ${data.videos} vidéo(s)`);
    } catch (err) {
      toast.error(formatApiError(err, "Impossible de lire ce dossier/album."));
    } finally {
      setAlbumTesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/media/${editingId}`, form);
        toast.success("Élément mis à jour");
      } else {
        await api.post("/admin/media", form);
        toast.success("Élément ajouté à la galerie");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({ kind: item.kind, title: item.title, url: item.url, description: item.description || "", dance: item.dance || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élément de la galerie ?")) return;
    await api.delete(`/admin/media/${id}`).catch(() => {});
    toast.success("Élément supprimé");
    load();
  };

  const kindLabel = (k) => KINDS.find((x) => x.value === k)?.label || k;

  return (
    <div data-testid="media-manager">
      {/* Album Google partagé */}
      <div className="mb-10 max-w-3xl rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded bg-[#B83227] text-white">
            <FolderOpen className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold uppercase text-[#1E1B18]">Album Google (dossier partagé)</h3>
            <p className="text-xs text-stone-500">Photos et vidéos affichées automatiquement en mosaïque sur la page Galerie</p>
          </div>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-stone-600">
          Collez le lien d'un <strong>dossier Google Drive</strong> ou d'un <strong>album Google Photos</strong> partagé publiquement
          (partage : « Toute personne disposant du lien »). Les photos et vidéos qu'il contient s'affichent sur la page Galerie —
          ajoutez-en dans Google, elles apparaîtront sur le site sous une heure, sans rien faire ici.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="album-url-input"
            className={inputClass}
            placeholder="https://drive.google.com/drive/folders/... ou https://photos.app.goo.gl/..."
            value={albumUrl}
            onChange={(e) => setAlbumUrl(e.target.value)}
          />
          <div className="flex shrink-0 gap-2">
            <button type="button" data-testid="album-test-button" onClick={testAlbum} disabled={albumTesting || !albumUrl} className="rounded border border-stone-300 px-4 py-2.5 text-xs font-bold uppercase text-stone-600 hover:border-stone-500 disabled:opacity-60">
              {albumTesting ? "Lecture..." : "Tester le lien"}
            </button>
            <button type="button" data-testid="album-save-button" onClick={saveAlbum} disabled={albumSaving} className="rounded bg-[#B83227] px-4 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
              {albumSaving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>

        <div className="mt-6 border-t border-stone-200 pt-5">
          <p className="text-xs font-bold uppercase text-stone-700">Affichage de la galerie publique</p>
          <label className="mt-3 flex items-center gap-2 text-sm text-stone-700">
            <input
              data-testid="gallery-random-checkbox"
              type="checkbox"
              checked={galleryRandom}
              onChange={(e) => setGalleryRandom(e.target.checked)}
              className="h-4 w-4 accent-[#B83227]"
            />
            Mélanger : 50 photos/vidéos au hasard à chaque visite
          </label>
          <p className="mt-1.5 text-xs leading-relaxed text-stone-500">
            Case décochée : la galerie affiche à la place les 50 photos/vidéos les plus récentes.
          </p>
          <button type="button" onClick={saveGalleryMode} data-testid="gallery-mode-save-button" disabled={modeSaving} className="mt-3 rounded bg-[#1E1B18] px-4 py-2.5 text-xs font-bold uppercase text-white hover:bg-stone-800 disabled:opacity-60">
            {modeSaving ? "Enregistrement..." : "Enregistrer le mode d'affichage"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">
          {editingId ? "Modifier l'élément" : "Ajouter à la galerie"}
        </h2>
        <div>
          <label className={labelClass}>Type *</label>
          <select data-testid="media-kind-select" className={inputClass} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Titre *</label>
          <input data-testid="media-title-input" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label className={labelClass}>Lien *</label>
          <input data-testid="media-url-input" className={inputClass} placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, url: u })), form.kind)} required />
          <p className="mt-1 text-xs text-stone-500">{KINDS.find((k) => k.value === form.kind)?.hint} {GOOGLE_HINT}</p>
          {form.kind !== "video" && (
            <div className="mt-2">
              <FileUploadButton
                testId="media-upload-button"
                accept={form.kind === "audio" ? "audio/*" : "image/*"}
                label={form.kind === "audio" ? "Ou téléverser un fichier audio" : "Ou téléverser une image"}
                onUploaded={(url) => setForm((f) => ({ ...f, url }))}
              />
            </div>
          )}
        </div>
        <div>
          <label className={labelClass}>Description (facultatif)</label>
          <input data-testid="media-description-input" className={inputClass} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        {form.kind === "audio" && (
          <div>
            <label className={labelClass}>Danse de ce morceau</label>
            <select data-testid="media-dance-select" className={inputClass} value={form.dance || ""} onChange={(e) => setForm({ ...form, dance: e.target.value })}>
              {DANCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-stone-500">Sur la page Audio, les petits danseurs danseront cette danse pendant la lecture.</p>
          </div>
        )}
        <div className="flex gap-3">
          <button type="submit" data-testid="media-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
            {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter"}
          </button>
          {editingId && (
            <button type="button" data-testid="media-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Dans la galerie ({items.length})</h2>
        {items.length === 0 && <p className="text-sm text-stone-500">Aucun élément.</p>}
        {items.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="flex items-center gap-4 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            {m.kind === "photo" && <img src={toImageUrl(m.url)} alt="" onError={(e) => (e.currentTarget.style.display = "none")} className="h-14 w-14 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-[#1E1B18]">{m.title}</p>
              <p className="text-xs text-stone-500">{kindLabel(m.kind)}</p>
            </div>
            <button onClick={() => edit(m)} data-testid={`media-edit-${m.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}
